import React, { useRef, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Renders a club name with proper truncation and a tap-to-reveal behavior:
 * on mobile (or whenever the text is actually clipped), tapping the label
 * fires a toast with the full name for 2s. On desktop the native `title`
 * tooltip kicks in on hover.
 *
 * Usage: drop-in replacement for a <span>/<p>. Pass `as="p"` to render a
 * paragraph instead of a span.
 */
const ClubNameLabel = ({
  name,
  as: Tag = 'span',
  className = '',
  style,
  truncate = true,
  ...rest
}) => {
  const ref = useRef(null);

  const handleClick = useCallback((e) => {
    const el = ref.current;
    if (!el || !name) return;
    // Only toast if the text is actually being clipped — no-op otherwise
    const isClipped = el.scrollWidth > el.clientWidth + 1;
    if (isClipped) {
      e.stopPropagation();
      toast(name, { duration: 2000 });
    }
  }, [name]);

  const baseStyle = truncate
    ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, ...style }
    : style;

  const baseClass = truncate
    ? `${className} overflow-hidden text-ellipsis whitespace-nowrap min-w-0 cursor-pointer`
    : className;

  return (
    <Tag
      ref={ref}
      className={baseClass}
      style={baseStyle}
      title={name || ''}
      onClick={handleClick}
      {...rest}
    >
      {name || '?'}
    </Tag>
  );
};

export default ClubNameLabel;
